import React from "react";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhoneAlt,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faArrowRight,
  faBolt,
  faShieldAlt,
  faChartLine,
  faComments,
} from "@fortawesome/free-solid-svg-icons";

const Contact = () => {
  const { webinfo = {} } = useLocalContext();
  const navigate = useNavigate();

  const brand = (webinfo.name || "our team").trim();
  const phoneCall = (webinfo.phoneCall || webinfo.phonecall || "").trim();
  const phoneDisplay = (webinfo.phone || phoneCall || "").trim();
  const email = (webinfo.email || "").trim();
  const address = (webinfo.address || "India").trim();
  const city = (webinfo.addressCity || "India").trim();
  const wa = (webinfo.whatsapp || webinfo.phonewhatsapp || phoneCall || "").trim();

  const waHref = wa ? `https://wa.me/${wa.replace(/[^\d]/g, "")}` : "/contact";

  return (
    <section className="cx">
      {/* HERO */}
      <header className="cx__hero">
        <div className="cx__heroWrap">
          <div className="cx__heroLeft">
            <span className="cx__badge">Contact</span>

            <h1 className="cx__title">
              Let’s plan your next move — <span>fast, clear, and measurable</span>.
            </h1>

            <p className="cx__sub">
              Tell us what you’re trying to achieve. We’ll reply with{" "}
              <b>quick wins</b>, a recommended plan, and a realistic timeline.
              Typical response time: <b>2–24 hours</b>.
            </p>

            <div className="cx__trust">
              <span className="cxPill">
                <FontAwesomeIcon icon={faBolt} /> Fast turnaround
              </span>
              <span className="cxPill">
                <FontAwesomeIcon icon={faChartLine} /> ROI-first approach
              </span>
              <span className="cxPill">
                <FontAwesomeIcon icon={faShieldAlt} /> Clean & secure builds
              </span>
            </div>

            <div className="cx__actions">
              <a className="cxBtn" href={waHref} target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faComments} /> WhatsApp us <FontAwesomeIcon icon={faArrowRight} />
              </a>

              <button className="cxBtn ghost" onClick={() => navigate("/services")}>
                Explore Services
              </button>
            </div>

            <div className="cx__note">
              Prefer a call?{" "}
              {phoneCall ? (
                <a href={`tel:${phoneCall}`}>Tap to call {brand}</a>
              ) : (
                <span className="muted">Add phoneCall in webinfo</span>
              )}
            </div>
          </div>

          <div className="cx__heroRight">
            <div className="cx__miniGrid">
              <div className="cxMini">
                <span className="k">Best for</span>
                <b>Brands, Startups</b>
                <span className="s">Growth + delivery partner</span>
              </div>
              <div className="cxMini">
                <span className="k">Next step</span>
                <b>Quick audit</b>
                <span className="s">We review your site & goal</span>
              </div>
              <div className="cxMini">
                <span className="k">Call window</span>
                <b>15–30 mins</b>
                <span className="s">Clarity + roadmap</span>
              </div>
            </div>

            <div className="cx__steps">
              <h3>What happens next</h3>
              <div className="cxStep">
                <span className="n">01</span>
                <div>
                  <b>We review your details</b>
                  <p>Goal, budget, timeline, and current setup.</p>
                </div>
              </div>
              <div className="cxStep">
                <span className="n">02</span>
                <div>
                  <b>You get a clear plan</b>
                  <p>Scope + priority steps + realistic ETA.</p>
                </div>
              </div>
              <div className="cxStep">
                <span className="n">03</span>
                <div>
                  <b>We start in sprints</b>
                  <p>Weekly delivery + measurable improvements.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <span className="cx__glow cx__glow1" aria-hidden="true" />
        <span className="cx__glow cx__glow2" aria-hidden="true" />
      </header>

      {/* BODY GRID */}
      <div className="cx__wrap">
        <div className="cx__grid">
          {/* LEFT: Contact cards */}
          <aside className="cxPanel">
            <div className="cxPanel__head">
              <h2>Direct contact</h2>
              <p className="muted">
                If you’re in a hurry, use these. Otherwise, send the form.
              </p>
            </div>

            <div className="cxCards">
              <div className="cxCard">
                <span className="cxIco">
                  <FontAwesomeIcon icon={faPhoneAlt} />
                </span>
                <div className="cxTxt">
                  <b>Call</b>
                  {phoneCall ? (
                    <a href={`tel:${phoneCall}`}>{phoneDisplay}</a>
                  ) : (
                    <span className="muted">Add phoneCall in webinfo</span>
                  )}
                  <small className="muted">Mon–Sat • 10:00 AM – 7:00 PM IST</small>
                </div>
              </div>

              <div className="cxCard">
                <span className="cxIco">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <div className="cxTxt">
                  <b>Email</b>
                  {email ? (
                    <a href={`mailto:${email}`}>{email}</a>
                  ) : (
                    <span className="muted">Add email in webinfo</span>
                  )}
                  <small className="muted">Best for proposals & documents</small>
                </div>
              </div>

              <div className="cxCard">
                <span className="cxIco">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </span>
                <div className="cxTxt">
                  <b>Location</b>
                  <span>{address}</span>
                  <small className="muted">Remote-first • Global delivery</small>
                </div>
              </div>

              <div className="cxCard">
                <span className="cxIco">
                  <FontAwesomeIcon icon={faClock} />
                </span>
                <div className="cxTxt">
                  <b>Working hours</b>
                  <span>Mon – Sat • 10 AM – 7 PM IST</span>
                  <small className="muted">Calls available across timezones</small>
                </div>
              </div>
            </div>

            <div className="cxPartner">
              <h3>Partnerships</h3>
              <p className="muted">
                Agencies & creators welcome. If you need a reliable delivery partner, let’s collaborate.
              </p>
              {email ? (
                <a className="cxPartner__link" href={`mailto:${email}`}>
                  {email} <FontAwesomeIcon icon={faArrowRight} />
                </a>
              ) : (
                <span className="muted">Add email in webinfo</span>
              )}
            </div>
          </aside>

          {/* RIGHT: Form */}
          <section className="cxPanel cxPanel--form">
            <div className="cxPanel__head">
              <h2>Start a project</h2>
              <p className="muted">
                Share details — we’ll reply with a plan, estimated timeline, and next steps.
              </p>
            </div>

            <form className="cxForm" onSubmit={(e) => e.preventDefault()}>
              <div className="cxRow">
                <label className="cxField">
                  <span>Name</span>
                  <input type="text" placeholder="Your name" required />
                </label>

                <label className="cxField">
                  <span>Email</span>
                  <input type="email" placeholder="you@email.com" required />
                </label>
              </div>

              <div className="cxRow">
                <label className="cxField">
                  <span>Phone / WhatsApp</span>
                  <input type="tel" placeholder="+91…" />
                </label>

                <label className="cxField">
                  <span>What do you need?</span>
                  <select defaultValue="" required>
                    <option value="" disabled>
                      Select one
                    </option>
                    <option>Website / Landing Page</option>
                    <option>SEO</option>
                    <option>Paid Ads</option>
                    <option>Automation / CRM</option>
                    <option>Full-stack / SaaS</option>
                    <option>eCommerce</option>
                  </select>
                </label>
              </div>

              <div className="cxRow">
                <label className="cxField">
                  <span>Budget range</span>
                  <select defaultValue="">
                    <option value="" disabled>
                      Select budget
                    </option>
                    <option>₹25k – ₹60k</option>
                    <option>₹60k – ₹1.5L</option>
                    <option>₹1.5L – ₹4L</option>
                    <option>₹4L+</option>
                  </select>
                </label>

                <label className="cxField">
                  <span>Timeline</span>
                  <select defaultValue="">
                    <option value="" disabled>
                      Select timeline
                    </option>
                    <option>ASAP</option>
                    <option>1–2 weeks</option>
                    <option>2–4 weeks</option>
                    <option>Flexible</option>
                  </select>
                </label>
              </div>

              <label className="cxField">
                <span>Project goal</span>
                <textarea rows="6" placeholder="What are you building? What’s the outcome you want?" required />
              </label>

              <button className="cxSubmit" type="submit">
                Send request <FontAwesomeIcon icon={faArrowRight} />
              </button>

              <p className="cxSmall">
                By submitting, you agree to be contacted about your request. No spam.
              </p>
            </form>
          </section>
        </div>

        {/* MAP */}
        <div className="cxMapCard">
          <div className="cxMapHead">
            <h3>Find us</h3>
            <span className="muted">{city}</span>
          </div>

          <div className="cxMap">
            <iframe
              title="Office Location"
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609915975!2d72.74109703785945!3d19.082197839145994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a9d90e14b%3A0x838fe179b1bdc674!2s${encodeURIComponent(
                city
              )}!5e0!3m2!1sen!2sin!4v1719999999999`}
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;